import React, { useState, useRef, useEffect } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import MicIcon from '@mui/icons-material/Mic';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { SearchIcon } from "lucide-react";

const SpeechToTextButton = ({ onTranscript }) => {
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null);

    useEffect(() => {
        if ('webkitSpeechRecognition' in window) {
            const SpeechRecognition = window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = false;
            recognitionRef.current.interimResults = false;
            recognitionRef.current.lang = 'en-US';

            recognitionRef.current.onresult = (event) => {
                const transcript = event?.results[0][0]?.transcript;
                onTranscript(transcript);
            };

            recognitionRef.current.onerror = (event) => {
                console.error("Speech recognition error", event?.error);
                setIsListening(false);
            };

            recognitionRef.current.onend = () => {
                setIsListening(false);
            };
        }
    }, [onTranscript]);

    const handleSpeechToText = () => {
        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        } else {
            recognitionRef.current.start();
            setIsListening(true);
        }
    };

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'inline-block',
                padding: isListening ? '8px' : '4px', // Added padding that increases when recording
                borderRadius: '12px',
                backgroundColor: isListening ? 'rgba(255, 68, 68, 0.05)' : 'transparent',
                transition: 'all 0.3s ease',
            }}
        >
            {/* Animated recording indicator ring */}
            {isListening && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 2,
                        left: 2,
                        right: 2,
                        bottom: 2,
                        borderRadius: '12px',
                        border: '2px solid #ff4444',
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                            '0%': {
                                transform: 'scale(1)',
                                opacity: 1,
                            },
                            '50%': {
                                transform: 'scale(1.05)',
                                opacity: 0.6,
                            },
                            '100%': {
                                transform: 'scale(1)',
                                opacity: 1,
                            },
                        },
                    }}
                />
            )}
            
            <IconButton
                onClick={handleSpeechToText}
                aria-label="voice search"
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    border: isListening ? "2px solid #ff4444" : "1px solid #e0e0e0",
                    borderRadius: '10px',
                    backgroundColor: isListening ? "#ffffff" : "#fafafa",
                    boxShadow: isListening 
                        ? "0 4px 12px rgba(255, 68, 68, 0.25), 0 2px 4px rgba(0,0,0,0.1)" 
                        : "0 2px 8px rgba(0,0,0,0.08)",
                    padding: '12px',
                    minWidth: '48px',
                    minHeight: '48px',
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                        backgroundColor: isListening ? "#fff5f5" : "#f0f9ff",
                        transform: "translateY(-1px)",
                        boxShadow: isListening 
                            ? "0 6px 16px rgba(255, 68, 68, 0.3), 0 4px 8px rgba(0,0,0,0.12)" 
                            : "0 4px 12px rgba(0,0,0,0.12)",
                    },
                    "&:active": {
                        transform: "translateY(0px)",
                    },
                }}
            >
                {isListening ? (
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            gap: 1,
                            position: 'relative'
                        }}
                    >
                        {/* Recording indicator dot */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -2,
                                right: -2,
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: '#ff4444',
                                boxShadow: '0 0 0 2px white, 0 0 8px rgba(255, 68, 68, 0.6)',
                                animation: 'blink 1s infinite',
                                '@keyframes blink': {
                                    '0%, 50%': { opacity: 1 },
                                    '51%, 100%': { opacity: 0.4 },
                                },
                            }}
                        />
                        <SearchIcon 
                            size={20} 
                            color="#ff4444" 
                        />
                    </Box>
                ) : (
                    <SearchIcon 
                        size={20} 
                        color="#666666" 
                    />
                )}
            </IconButton>
            
            {/* Optional recording status text */}
            {isListening && (
                <Typography 
                    variant="caption" 
                    sx={{
                        position: 'absolute',
                        bottom: -20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: '#ff4444',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap',
                        animation: 'fadeIn 0.3s ease-in',
                        '@keyframes fadeIn': {
                            '0%': { opacity: 0, transform: 'translateX(-50%) translateY(-5px)' },
                            '100%': { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
                        },
                    }}
                >
                    Listening...
                </Typography>
            )}
        </Box>
    );
};

export default SpeechToTextButton;
